import { ErrorHandler, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from 'environments/environment';
import { Metar } from '../models/metar.model';
import { Taf } from '../models/taf.model';
import { LatLon } from '../../util/geodesy/latlon';


export interface IWeatherService {
  getMetar(identifier: string): Promise<Metar>;
  getMetarsByLatLon(latlon: LatLon, radius?: number, maximum?: number): Promise<Array<Metar>>;
  getTaf(identifier: string): Promise<Taf>;
  getTafsByLatLon(latlon: LatLon, radius?: number, maximum?: number): Promise<Array<Taf>>;
}


/**
 * Default radius (in nautical miles) to use when querying for METARs by lat/lon
 * @type {number}
 */
const DEFAULT_METAR_RADIUS = 25;


/**
 * Default radius (in nautical miles) to use when querying for TAFs by lat/lon
 * @type {number}
 */
const DEFAULT_TAF_RADIUS = 50;


/**
 * Service for querying weather information (METARs and TAFs)
 */
@Injectable()
export class WeatherService implements IWeatherService {
  constructor(
    private _http: HttpClient,
    private _errorHandler: ErrorHandler
  ) { }

  /**
   * Returns the latest METAR for a given station identifier
   * @param {string} identifier Station identifier
   * @returns {Promise<Metar>} METAR
   */
  async getMetar(identifier: string): Promise<Metar> {
    try {
      // const response = await this._http.get<Array<any>>(`${environment.weatherApiUrl}/metars?body=["${identifier}"]`).toPromise();
      const response = await this._http.get<Array<any>>(`${environment.airportsApiUrl}/wx/metars?query=${identifier}`).toPromise();
      const data = response.find(d => d.icao_code === identifier);
      return data ? Metar.parse(data) : null;
    } catch (e) {
      if (e && e.status === 404) {
        return null;
      }
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Queries for METARs within a given distance of a location
   * @param {LatLon} latlon Location
   * @param {number} [radius] Radius distance (in nautical miles)
   * @param {number} [maximum] Maximum number of results to query. If not specified, all results within the given
   *  distance will be returned.
   * @returns {Promise<Array<Metar>>} Latest METARs within the specified area
   */
  async getMetarsByLatLon(latlon: LatLon, radius: number = DEFAULT_METAR_RADIUS, maximum: number = null): Promise<Array<Metar>> {
    try {
      const body: any = {
        latitude: latlon.lat,
        longitude: latlon.lon,
        radius: radius
      };
      if (maximum !== null) {
        body.maximum = maximum;
      }
      // const url = `${environment.weatherApiUrl}/metars?body=${encodeURIComponent(JSON.stringify(body))}`;
      const url = `${environment.airportsApiUrl}/wx/metars?latitude=${latlon.lat}&longitude=${latlon.lon}&radius=${radius}`;
      const response = await this._http.get<Array<any>>(url).toPromise();
      return response.map(Metar.parse);
    } catch (e) {
      if (e && e.status === 404) {
        return null;
      }
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns the latest TAF for a given station identifier
   * @param {string} identifier Station identifier
   * @returns {Promise<Taf>} TAF
   */
  async getTaf(identifier: string): Promise<Taf> {
    try {
      // const response = await this._http.get<Array<any>>(`${environment.weatherApiUrl}/tafs?body=["${identifier}"]`).toPromise();
      const response = await this._http.get<Array<any>>(`${environment.airportsApiUrl}/wx/tafs?query=${identifier}`).toPromise();
      const data = response.find(d => d.icao_code === identifier);
      return data ? Taf.parse(data) : null;
    } catch (e) {
      if (e && e.status === 404) {
        return null;
      }
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Queries for TAFs within a given distance of a location
   * @param {LatLon} latlon Location
   * @param {number} [radius] Radius distance (in nautical miles)
   * @param {number} [maximum] Maximum number of results to query. If not specified, all results within the given
   *  distance will be returned.
   * @returns {Promise<Array<Taf>>} Latest TAFs within the specified area
   */
  async getTafsByLatLon(latlon: LatLon, radius: number = DEFAULT_TAF_RADIUS, maximum: number = null): Promise<Array<Taf>> {
    try {
      const body: any = {
        latitude: latlon.lat,
        longitude: latlon.lon,
        radius: radius
      };
      if (maximum !== null) {
        body.maximum = maximum;
      }
      // const url = `${environment.weatherApiUrl}/tafs?body=${encodeURIComponent(JSON.stringify(body))}`;
      const url = `${environment.airportsApiUrl}/wx/tafs?latitude=${latlon.lat}&longitude=${latlon.lon}&radius=${radius}`;
      const response = await this._http.get<Array<any>>(url).toPromise();
      return response.map(Taf.parse);
    } catch (e) {
      if (e && e.status === 404) {
        return null;
      }
      this._errorHandler.handleError(e);
      throw e;
    }
  }
}
